
import React from 'react';
import { Ball, SimulationConfig, MorphData, EscapeRing } from '@/types';
import { EscapeService } from '@/services/EscapeService';
import { isInsideCollider } from '../physics/BoundaryCollision';
import { drawEscapeRings } from '@/render/EscapeRenderer';

export const EscapeLogic = {
  init: (
    effectiveCfg: SimulationConfig,
    centerX: number,
    centerY: number,
    escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
    initialEscapeRingCountRef: React.MutableRefObject<number>,
    timeRemainingRef: React.MutableRefObject<number>,
    escapeAccelerationBoostRef: React.MutableRefObject<number>,
    createBall: (overrides: Partial<Ball>) => Ball,
    getInitialBallState: (index: number, total: number, config?: any) => { pos: { x: number, y: number }, vel: { x: number, y: number } },
    now: number = Date.now()
  ): Ball[] => {
    timeRemainingRef.current = effectiveCfg.escapeTimerStart || 10;
    escapeAccelerationBoostRef.current = 0;
    escapeRingsRef.current = EscapeService.generateRings(effectiveCfg);
    
    if (effectiveCfg.outsideColliderEnabled && effectiveCfg.outsideColliderCulling) {
        escapeRingsRef.current = escapeRingsRef.current.filter(ring => 
            isInsideCollider(0, 0, ring.radius, effectiveCfg, effectiveCfg.outsideColliderCullingMargin)
        );
    }
    
    initialEscapeRingCountRef.current = escapeRingsRef.current.length; 
    const total = Math.max(1, effectiveCfg.initialBalls || 1);
    
    return Array.from({ length: total }).map((_, i) => {
        const individualConfig = effectiveCfg.ballPlacementIndividualEnabled && effectiveCfg.ballPlacementConfigs && (effectiveCfg.ballPlacementConfigs as any)[i] 
            ? (effectiveCfg.ballPlacementConfigs as any)[i] 
            : null;
        const { pos, vel } = getInitialBallState(i, total, individualConfig);
        return createBall({
            id: `escape-ball-${i}`,
            radius: effectiveCfg.ballRadius,
            color: individualConfig?.color || (i === 0 ? effectiveCfg.ballColor : (i === 1 ? effectiveCfg.ball2Color : '#ffffff')),
            pos,
            vel,
            spawnTime: now
        });
    });
  },

  updateRings: (
    ring: EscapeRing,
    effectiveCfg: SimulationConfig,
    currentTS: number,
    idx: number,
    isRotationStopped: boolean,
    hyperSpeedActive: boolean,
    remainingRingsCount: number,
    minRadiusOverride?: number
  ) => {
    if (isNaN(ring.radius) || !isFinite(ring.radius)) ring.radius = 100;
    const rotationActive = !isRotationStopped && effectiveCfg.escapeRotationMode !== 'NONE';
    const shouldShrink = effectiveCfg.escapeShrinkMode && !effectiveCfg.escapeStaticMode && !(effectiveCfg.enableHyperSpeed && effectiveCfg.hyperSpeedStopShrink && hyperSpeedActive);
    
    if (shouldShrink) {
      let shrinkRate = effectiveCfg.escapeShrinkRate;
      if (effectiveCfg.escapeDynamicShrink) {
          const progress = (effectiveCfg.escapeRingCount - remainingRingsCount) / Math.max(1, effectiveCfg.escapeRingCount);
          shrinkRate *= (1 + progress * 3);
      }
      const baseShrink = shrinkRate * currentTS;
      const minRadiusForRing = Math.max(minRadiusOverride || 0, effectiveCfg.escapeMinRadius + idx * effectiveCfg.escapeRingSpacing);
      let nextRadius = ring.radius - baseShrink;
      if (isNaN(nextRadius) || !isFinite(nextRadius)) nextRadius = ring.radius;
      ring.radius = Math.max(minRadiusForRing, nextRadius);
    }
    
    if (rotationActive) {
      const rotationIdx = ring.originalIndex !== undefined ? ring.originalIndex : idx;
      let dir = (effectiveCfg.escapeRotationMode === 'ALTERNATING') ? (rotationIdx % 2 === 0 ? 1 : -1) : (effectiveCfg.escapeRotationMode === 'COUNTER_CLOCKWISE' ? -1 : 1);
      ring.angle += effectiveCfg.escapeRotationSpeed * dir * currentTS * 0.01;
    }
  },

  render: (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, rings: EscapeRing[], config: SimulationConfig, hue: number, morphData: MorphData, webgl: any, cam: any, initialCount: number, timeRemaining: number, balls: Ball[], counterPop: number, zoomValue: number) => {
      drawEscapeRings(ctx, centerX, centerY, rings, config, hue, morphData, false, cam, window.innerWidth, window.innerHeight);
  }
};
